import { forwardRef, useEffect, useRef, useState } from 'react';

import { Box, Collapse, Group, PopoverProps, ScrollAreaAutosizeProps, Stack } from '@mantine/core';
import { IconChevronDown, IconFilter } from 'ui-icons';

import { ActionIcon } from '../../../../Buttons/ActionIcon/ActionIcon';
import { ButtonMantine, ButtonProps } from '../../../../Buttons/ButtonMantine';
import { useDisclosure } from '../../../../Hooks';
import { ScrollArea } from '../../../../Miscellaneous/ScrollArea';
import { Popover } from '../../../../Overlays/Popover/Popover';
import { Text } from '../../../../Typography/Text/Text';
import {
  FilterSearchContextValue,
  FilterSearchProvider,
  useFilterSearchContext,
} from './FilterSearch.context';

const POPOVER_WIDTH = 325;

interface SearchButtonProps extends ButtonProps {
  onClick?: () => void;
}
interface CollapseProps {
  title?: string;
  children?: React.ReactNode | React.ReactNode[];
  value?: string;
}

interface FilterSearchMenuProps extends Omit<PopoverProps, 'children'> {
  children?: React.ReactNode | React.ReactNode[];
  /** Action bar rendered under the list */
  actionBar?: React.ReactNode | React.ReactNode[];
}

interface FilterSearchContentProps {
  children?: React.ReactNode | React.ReactNode[];
  scrollAreaProps?: ScrollAreaAutosizeProps;
}

interface FilterSearchFooterProps {
  onClick: () => void;
}

/**
 * The props for the pre-composed FilterSearch component
 */
interface FilterSearchProps extends Omit<PopoverProps, 'children'> {
  children?: React.ReactNode | React.ReactNode[];
  /** Reset functionality, when provided renders a reset button */
  onReset?: () => void;
  /** Search functionality, when provided renders a search button */
  onSearch?: () => void;
  contentProps?: FilterSearchContentProps;
}

const SearchButton = forwardRef<HTMLButtonElement, SearchButtonProps>(
  (props: SearchButtonProps, ref) => {
    return (
      <Box
        component="button"
        style={{
          padding: '8px',
          width: '280px',
          border: `1px solid #EEEEEE`,
          borderRadius: '8px',
          backgroundColor: '#FAFAFA',
          color: '#666666',
        }}
        {...props}
        ref={ref}
      >
        <Group gap={'xs'} c="gray">
          <IconFilter size="1rem" stroke={1.5} />
          <Text
            style={{
              flexGrow: 1,
              textAlign: 'left',
            }}
            fz="sm"
            c="gray"
          >
            Filter
          </Text>
          <IconChevronDown size="0.75rem" stroke={1.5} />
        </Group>
      </Box>
    );
  }
);
SearchButton.displayName = 'FilterSearchButton';

const FilterSearchItem = ({
  children,
}: {
  children?: (
    rootRef?: React.RefObject<HTMLDivElement> | null
  ) => React.ReactNode | React.ReactNode[];
}) => {
  const { rootRef } = useFilterSearchContext();

  return (
    <Stack
      gap="md"
      p="1px" // Prevent borders from being cut off
    >
      {children?.(rootRef)}
    </Stack>
  );
};

const FilterSearchCollapse = forwardRef<HTMLDivElement, CollapseProps>(
  (props: CollapseProps, ref) => {
    const [opened, handlers] = useDisclosure(false);

    useEffect(() => {
      // When this component mounts, we want we shouldn't open it if there are any filters
      if (typeof props.value === 'string' && props.value.length > 0) {
        handlers.open();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps -- No need to update on handler
    }, [props.value]);

    return (
      <Stack>
        <Group
          justify="space-between"
          onClick={() => handlers.toggle()}
          style={{ cursor: 'pointer' }}
          wrap="nowrap"
        >
          <Text type="label">{props.title}</Text>
          <ActionIcon
            size={'sm'}
            c="gray"
            variant="transparent"
            style={{
              transition: 'all 200ms ease-in-out',
              rotate: opened ? '180deg' : '360deg',
            }}
          >
            <IconChevronDown />
          </ActionIcon>
        </Group>
        <Collapse in={opened} ref={ref}>
          {props.children}
        </Collapse>
      </Stack>
    );
  }
);
FilterSearchCollapse.displayName = 'FilterSearchCollapse';

const FilterSearchContent = ({ children, scrollAreaProps }: FilterSearchContentProps) => {
  // The below lines will ensure we get a state update when the rootRef is set thereby ensuring any FilterSearchItem has access to the latest ref
  const rootRef = useRef<HTMLDivElement>(null);
  const { setRootRef } = useFilterSearchContext();
  useEffect(() => {
    setRootRef?.(rootRef);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- This is a state setter that should only run once
  }, []);

  return (
    <Box ref={rootRef}>
      <ScrollArea.Autosize mah="500px" {...scrollAreaProps}>
        <Stack
          gap="md"
          p="5px" // Prevent borders from being cut off
          maw={POPOVER_WIDTH - 24} // NOTE: 24 is driven by the padding "sm" set on the Popover.Dropdown above
        >
          {children}
        </Stack>
      </ScrollArea.Autosize>
    </Box>
  );
};

const FilterSearchResetButton = ({ onClick }: FilterSearchFooterProps) => {
  return (
    <ButtonMantine variant="subtle" size="sm" onClick={onClick}>
      Clear
    </ButtonMantine>
  );
};
const FilterSearchSearchButton = ({ onClick }: FilterSearchFooterProps) => {
  return (
    <ButtonMantine size="sm" onClick={onClick}>
      Search
    </ButtonMantine>
  );
};

const FilterSearchActionBar = ({
  children,
}: {
  children?: React.ReactNode | React.ReactNode[];
}) => {
  // Even when using this component solo, these actions should be set via the root
  const { onReset, onSearch } = useFilterSearchContext();

  if (children) {
    return (
      <Group justify="space-between" grow py="xs">
        {children}
      </Group>
    );
  }
  return (
    <Group justify="space-between" grow py="xs">
      {onReset && <FilterSearchResetButton onClick={onReset} />}
      {onSearch && <FilterSearchSearchButton onClick={onSearch} />}
    </Group>
  );
};

const FilterSearchMenu = ({
  children,
  portalProps,
  keepMounted,
  actionBar,
}: FilterSearchMenuProps) => {
  const [opened, setOpened] = useState(false);

  return (
    <Popover
      width={POPOVER_WIDTH}
      position="bottom-end"
      shadow="md"
      opened={opened}
      onChange={setOpened}
      portalProps={portalProps}
      keepMounted={keepMounted}
    >
      <Popover.Target>
        <SearchButton onClick={() => setOpened((prev) => !prev)} />
      </Popover.Target>
      <Popover.Dropdown p="sm">
        {children}
        <FilterSearchActionBar>{actionBar}</FilterSearchActionBar>
      </Popover.Dropdown>
    </Popover>
  );
};

const FilterSearchRoot = ({ children, ...rest }: FilterSearchContextValue) => {
  const [rootRef, setRootRef] = useState<React.RefObject<HTMLDivElement> | null>(null);
  return (
    <FilterSearchProvider value={{ rootRef, setRootRef: setRootRef, ...rest }}>
      {children}
    </FilterSearchProvider>
  );
};

const useFilterSearchDefaultProps = (props: FilterSearchProps) => {
  const { children, contentProps, onReset, onSearch, ...rest } = props;
  const rootProps = {
    onReset: onReset,
    onSearch: onSearch,
  };
  return {
    children,
    contentProps,
    rootProps,
    ...rest,
  };
};

// All in one filter component and provider, just pass in children & props
const FilterSearch = (props: FilterSearchProps) => {
  const { children, contentProps, rootProps, ...rest } = useFilterSearchDefaultProps(props);
  return (
    <FilterSearchRoot {...rootProps}>
      <FilterSearchMenu {...rest}>
        <FilterSearchContent {...contentProps}>{children}</FilterSearchContent>
      </FilterSearchMenu>
    </FilterSearchRoot>
  );
};

FilterSearch.Root = FilterSearchRoot;
FilterSearch.Button = SearchButton;
FilterSearch.Collapse = FilterSearchCollapse;
FilterSearch.Content = FilterSearchContent;
FilterSearch.Menu = FilterSearchMenu;
FilterSearch.ResetButton = FilterSearchResetButton;
FilterSearch.Actions = FilterSearchActionBar;
FilterSearch.Item = FilterSearchItem;

export { FilterSearch };
